/**
 * Helpers for exporting dashboard data to CSV and PDF.
 * CSV: pure JS, no dependency.
 * PDF: jspdf + jspdf-autotable for structured tables.
 */
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/* -------------------- string sanitising -------------------- */
// jsPDF's built-in Helvetica cannot render narrow no-break space (U+202F),
// thin space (U+2009) or non-breaking space (U+00A0) — the glyphs come out as
// vertical slashes / gaps. Replace them by a normal ASCII space so the PDF
// stays readable. Also normalises "…" if font can't render it.
const PDF_TRANSLATIONS = [
  [/\u202F/g, " "],
  [/\u2009/g, " "],
  [/\u00A0/g, " "],
  [/\u2013/g, "-"],
  [/\u2014/g, "-"],
  [/\u2026/g, "..."],
];

function pdfSafe(v) {
  if (v == null) return "";
  let s = String(v);
  for (const [re, sub] of PDF_TRANSLATIONS) s = s.replace(re, sub);
  return s;
}

function pdfSafeRow(row) {
  return row.map(pdfSafe);
}

/* -------------------- CSV -------------------- */
function csvEscape(v) {
  if (v == null) return "";
  const s = pdfSafe(v);
  if (s.includes(",") || s.includes('"') || s.includes("\n") || s.includes(";")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/**
 * sections: [{ title: string, headers: string[], rows: (string|number)[][] }]
 */
export function downloadCsv(filename, sections) {
  const lines = [];
  sections.forEach((sec, i) => {
    if (i > 0) lines.push("");
    if (sec.title) lines.push(csvEscape(sec.title));
    if (sec.headers?.length) lines.push(sec.headers.map(csvEscape).join(","));
    (sec.rows || []).forEach((row) => {
      lines.push(row.map(csvEscape).join(","));
    });
  });
  // BOM for Excel UTF-8 compatibility
  const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  triggerDownload(blob, filename);
}

/* -------------------- PDF -------------------- */
/**
 * config: {
 *   title: string,
 *   subtitle?: string,
 *   period?: string,
 *   generatedLabel?: string,
 *   sections: [{ title: string, headers: string[], rows: [][] } | { title: string, kvs: [[label, value]] }]
 * }
 */
export function downloadPdf(filename, config) {
  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const marginX = 40;
  let y = 50;

  // Header band
  doc.setFillColor(227, 38, 54); // brand-red
  doc.rect(0, 0, doc.internal.pageSize.getWidth(), 6, "F");

  // Brand
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(26, 26, 26);
  doc.text("NelDaari", marginX, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  doc.text("Conakry - Guinee", marginX + 78, y - 2);
  y += 18;

  // Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.setTextColor(26, 26, 26);
  doc.text(pdfSafe(config.title || "Dashboard"), marginX, y);
  y += 16;

  // Sub / meta
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(90, 90, 90);
  const metaParts = [];
  if (config.subtitle) metaParts.push(pdfSafe(config.subtitle));
  if (config.period) metaParts.push(pdfSafe(config.period));
  if (metaParts.length) {
    doc.text(metaParts.join("  -  "), marginX, y);
    y += 14;
  }
  if (config.generatedLabel) {
    doc.text(pdfSafe(config.generatedLabel), marginX, y);
    y += 14;
  }
  y += 4;

  // Sections
  (config.sections || []).forEach((sec) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(227, 38, 54);
    doc.text(pdfSafe(sec.title || ""), marginX, y);
    y += 8;

    if (sec.kvs?.length) {
      autoTable(doc, {
        startY: y,
        head: [],
        body: sec.kvs.map(([k, v]) => [pdfSafe(k), pdfSafe(v)]),
        theme: "plain",
        styles: { fontSize: 10, cellPadding: 4, textColor: [40, 40, 40] },
        columnStyles: {
          0: { fontStyle: "bold", cellWidth: 180 },
          1: { textColor: [26, 26, 26] },
        },
        margin: { left: marginX, right: marginX },
      });
      y = doc.lastAutoTable.finalY + 14;
    } else if (sec.headers?.length) {
      autoTable(doc, {
        startY: y,
        head: [pdfSafeRow(sec.headers)],
        body: (sec.rows || []).map(pdfSafeRow),
        theme: "striped",
        styles: { fontSize: 9, cellPadding: 4 },
        headStyles: { fillColor: [26, 26, 26], textColor: [255, 255, 255], fontStyle: "bold" },
        alternateRowStyles: { fillColor: [252, 250, 245] },
        margin: { left: marginX, right: marginX },
      });
      y = doc.lastAutoTable.finalY + 14;
    }
  });

  // Footer on every page
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(140, 140, 140);
    doc.text(
      `NelDaari · ${i} / ${pageCount}`,
      doc.internal.pageSize.getWidth() - marginX,
      doc.internal.pageSize.getHeight() - 20,
      { align: "right" }
    );
  }

  doc.save(filename);
}

/* -------------------- helpers -------------------- */
function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}

export function slugify(s) {
  return String(s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40) || "export";
}

export function todayStamp() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
}
